import { useTranslation } from "react-i18next";
import React, { useEffect, useState } from 'react';
import { DateRangePicker } from './DateRangePicker';
import { GranularitySelector, Granularity } from './GranularitySelector';
import { AuthenticationFilters } from './AuthenticationFilters';
import { EmptyState } from './EmptyState';
interface DateRange {
  start: Date;
  end: Date;
}
interface AuthenticationTabProps {
  dateRange: DateRange;
  granularity: Granularity;
  onDateRangeChange: (range: DateRange) => void;
  onGranularityChange: (granularity: Granularity) => void;
}
export function AuthenticationTab({
  dateRange,
  granularity,
  onDateRangeChange,
  onGranularityChange
}: AuthenticationTabProps) {
  const {
    t
  } = useTranslation("analytics");
  // Filter states - no successRateMetric for Authentication tab
  const [deduplication, setDeduplication] = useState<'deduplicated' | 'raw'>('deduplicated');
  const [cardTypes, setCardTypes] = useState<string[]>([]);
  const [cardCountries, setCardCountries] = useState<string[]>([]);
  const [cardBrands, setCardBrands] = useState<string[]>([]);
  const [interactionTypes, setInteractionTypes] = useState<string[]>([]);
  const [additionalFilters, setAdditionalFilters] = useState<string[]>([]);
  // Calculate previous period date range
  const getPreviousPeriodRange = () => {
    const days = Math.ceil((dateRange.end.getTime() - dateRange.start.getTime()) / (1000 * 60 * 60 * 24));
    const prevStart = new Date(dateRange.start);
    prevStart.setDate(prevStart.getDate() - days);
    const prevEnd = new Date(dateRange.end);
    prevEnd.setDate(prevEnd.getDate() - days);
    const formatDate = (date: Date) => {
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric'
      });
    };
    return `${formatDate(prevStart)} - ${formatDate(prevEnd)}`;
  };
  const handleClearFilters = () => {
    setDeduplication('deduplicated');
    setCardTypes([]);
    setCardCountries([]);
    setCardBrands([]);
    setInteractionTypes([]);
    setAdditionalFilters([]);
  };
  return <div className="space-y-6" data-id="element-1424" data-mp="18shagfiy3jw1">
      {/* Main Filters Row */}
      <div className="flex flex-wrap items-center gap-3" data-id="element-1425" data-mp="13z62gb2ecx2j">
        <DateRangePicker value={dateRange} onChange={onDateRangeChange} data-id="element-1426" data-mp="z0l7aq2053ky" />
        <GranularitySelector value={granularity as Granularity} onChange={onGranularityChange} data-id="element-1427" data-mp="yyi9vkyis867" />
        
        <AuthenticationFilters deduplication={deduplication} onDeduplicationChange={setDeduplication} cardTypes={cardTypes} onCardTypesChange={setCardTypes} cardCountries={cardCountries} onCardCountriesChange={setCardCountries} cardBrands={cardBrands} onCardBrandsChange={setCardBrands} interactionTypes={interactionTypes} onInteractionTypesChange={setInteractionTypes} additionalFilters={additionalFilters} onAdditionalFiltersChange={setAdditionalFilters} data-id="element-1428" data-mp="1rpig82kbpo11" />
        
      </div>

      {/* Key Metrics Section */}
      <div className="bg-white rounded-lg border border-gray-200" data-id="element-1429" data-mp="2au29ww33y0w">
        <div className="p-6 border-b border-gray-200" data-id="element-1430" data-mp="2cos9pm7qgbu">
          <div className="flex items-center justify-between" data-id="element-1431" data-mp="2dyyz3m0uzven">
            <div className="flex items-center gap-4" data-id="element-1432" data-mp="2hziw8plfmz0s">
              <h2 className="text-lg font-semibold text-gray-900" data-id="element-1433" data-mp="2i02ds5767lea">
                {t("components.AcceptanceTab.attrs.key_metrics")}
              </h2>
              <div className="flex items-center gap-2 text-sm text-gray-600" data-id="element-1434" data-mp="2rp2g9ykvsyx8">
                <span data-id="element-1435" data-mp="2rr6ub9chveb8">{t("components.AuthenticationTab.compared_to")}</span>
                <button className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-1436" data-mp="2wo5kxhwy934o">
                  {t("components.AuthenticationTab.previous_period")}
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" data-id="element-1437" data-mp="3any85399p17w">
                    
                    <path d="M4.427 9.573l3.396-3.396a.25.25 0 01.354 0l3.396 3.396a.25.25 0 01-.177.427H4.604a.25.25 0 01-.177-.427z" data-id="element-1438" data-mp="1bbm0maz5s9r6" />
                  </svg>
                </button>
                <span className="text-gray-500" data-id="element-1439" data-mp="l00f7vdhgf8g">
                  {getPreviousPeriodRange()}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="p-12" data-id="element-1440" data-mp="g30dtxrkw0no">
          <EmptyState message="No authentication requests matching this selection." submessage={<>
                <button onClick={handleClearFilters} className="text-blue-600 hover:underline" data-id="element-1442" data-mp="pyk70cercl8q">
                
                  {t("components.AuthenticationTab.clear_filters")}
                </button>{' '}
                {t("components.AuthenticationTab.or_select_another_date_range_and")}
              </>} data-id="element-1441" data-mp="uyq0ly4l8htg" />
          
        </div>
      </div>

      {/* 3D Secure authentication outcomes */}
      <div className="bg-white rounded-lg border border-gray-200" data-id="element-1443" data-mp="782fuc9kpxph">
        <div className="p-6 border-b border-gray-200" data-id="element-1444" data-mp="76rc2cg4opyd">
          <h2 className="text-lg font-semibold text-gray-900" data-id="element-1445" data-mp="3fndt3aitrup4">
            {t("components.AuthenticationTab.3d_secure_authentication_outcomes")}
          </h2>
        </div>

        <div className="p-12" data-id="element-1446" data-mp="1q6rwkx7ttc0g">
          <EmptyState message="No authentication requests matching this selection." submessage={<>
                <button onClick={handleClearFilters} className="text-blue-600 hover:underline" data-id="element-1448" data-mp="1uzl3nrqrowfp">
                
                  {t("components.AuthenticationTab.clear_filters")}
                </button>{' '}
                {t("components.AuthenticationTab.or_select_another_date_range_and")}
              </>} data-id="element-1447" data-mp="1q6i8jf6cdexe" />
          
        </div>
      </div>

      {/* 3D Secure authentication request reasons */}
      <div className="bg-white rounded-lg border border-gray-200" data-id="element-1449" data-mp="2dwckluiu6zgl">
        <div className="p-6 border-b border-gray-200" data-id="element-1450" data-mp="1zxuwvwsxlmyo">
          <h2 className="text-lg font-semibold text-gray-900" data-id="element-1451" data-mp="1zzfszquw9dyn">
            {t("components.AuthenticationTab.3d_secure_authentication_request_reasons")}
          </h2>
        </div>

        <div className="p-12" data-id="element-1452" data-mp="ogmvzlhhuoz9">
          <EmptyState message="No authentication requests matching this selection." submessage={<>
                <button onClick={handleClearFilters} className="text-blue-600 hover:underline" data-id="element-1454" data-mp="2v5x986gh8tru">
                
                  {t("components.AuthenticationTab.clear_filters")}
                </button>{' '}
                {t("components.AuthenticationTab.or_select_another_date_range_and")}
              </>} data-id="element-1453" data-mp="sjjivoe2h790" />
          
        </div>
      </div>
    </div>;
}